import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CurecallButton, ECurecallSize } from '@honestica/curecall-ui'
import {
  EHumanReviewResolution,
  SUBJECTS_WITH_EXTENDED_RESOLUTIONS,
  type EHumanReviewRequestSubject,
  type THumanReviewRequest,
} from '../human-review-requests.type'
import ResolveHrrModal, { type TResolveStep } from './resolve-hrr-modal.component'

const buildResolveSteps = (requests: THumanReviewRequest[]): TResolveStep[] => {
  const stepsBySubject = new Map<EHumanReviewRequestSubject, THumanReviewRequest[]>()
  for (const request of requests) {
    const stepRequests = stepsBySubject.get(request.subject) ?? []
    stepRequests.push(request)
    stepsBySubject.set(request.subject, stepRequests)
  }
  return [...stepsBySubject.entries()].map(([subject, stepRequests]) => ({
    subject,
    requests: stepRequests,
  }))
}

type Props = {
  unresolvedRequests: THumanReviewRequest[]
  patientMedEventId: number
  patient: { firstName: string | null; lastName: string | null }
}

export const ProblemResolutions = ({ unresolvedRequests, patientMedEventId, patient }: Props) => {
  const { t } = useTranslation()
  const [selectedResolution, setSelectedResolution] = useState<EHumanReviewResolution | null>(null)

  const resolveSteps = useMemo(() => buildResolveSteps(unresolvedRequests), [unresolvedRequests])

  if (unresolvedRequests.length === 0) return null

  const showExtendedResolutions = unresolvedRequests.some((request) =>
    SUBJECTS_WITH_EXTENDED_RESOLUTIONS.includes(request.subject),
  )

  return (
    <div className="c-flex c-flex-col c-gap-2 c-pt-4 c-pb-4 c-pl-4">
      <div className="c-flex c-gap-2">
        <CurecallButton
          size={ECurecallSize.SMALL}
          onClick={() => setSelectedResolution(EHumanReviewResolution.PROCESSED)}
        >
          {t('pages.dashboard.human-review-requests.problems.resolutions.mark-as-processed')}
        </CurecallButton>
        {showExtendedResolutions && (
          <>
            <CurecallButton
              size={ECurecallSize.SMALL}
              onClick={() => setSelectedResolution(EHumanReviewResolution.UNREACHABLE)}
            >
              {t('pages.dashboard.human-review-requests.problems.resolutions.mark-as-unreachable')}
            </CurecallButton>
            <CurecallButton
              size={ECurecallSize.SMALL}
              onClick={() => setSelectedResolution(EHumanReviewResolution.CONTACTED)}
            >
              {t('pages.dashboard.human-review-requests.problems.resolutions.mark-as-contacted')}
            </CurecallButton>
          </>
        )}
      </div>
      <ResolveHrrModal
        steps={resolveSteps}
        resolution={selectedResolution}
        patientMedEventId={patientMedEventId}
        patient={patient}
        onClose={() => setSelectedResolution(null)}
      />
    </div>
  )
}
